import React from "react";
import { Navigate, useNavigate } from "react-router-dom";

import OnboardingSection from "@/components/onboarding/OnboardingSection.jsx";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar.jsx";
import Button from "@/components/ui/Button.jsx";
import FirstUseCookieGate from "@/components/consent/FirstUseCookieGate.jsx";
import DoctorOnboarding from "@/pages/onboarding/doctor/DoctorOnboarding.jsx";
import ChangePassword from "@/pages/profile-settings/patient/ChangePassword.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import { advanceStagingFirstUse } from "@/services/stagingAccessService.js";

function ClinicianFirstUseStart({ onBack }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = React.useState(false);

  const continueToCookies = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await advanceStagingFirstUse("cookies");
      navigate("/onboarding/doctor?first_use=1&stage=cookies", { replace: true });
      window.setTimeout(() => window.afrimedCookies?.open?.(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={1} total={1} onBack={onBack} />
      <OnboardingSection
        header={
          <div className="space-y-2">
            <h1 className="text-title-large text-on-surface">Get started</h1>
            <p className="text-body-medium text-on-surface-var">
              We’ll guide you through the essentials to set up your AfriMed™ clinician workspace.
            </p>
          </div>
        }
        footer={
          <Button size="lg" disabled={submitting} onClick={continueToCookies}>
            Get started
          </Button>
        }
      />
    </div>
  );
}

/**
 * Server-governed first-use adapter for the clinician lane. The stage is
 * resolved by resolve-staging-first-use; it is never derived from a query
 * parameter or browser-selected role.
 */
export default function ControlledDoctorFirstUse({ stage, backToCategory }) {
  const navigate = useNavigate();

  switch (stage) {
    case "get_started":
      return <ClinicianFirstUseStart onBack={() => navigate("/login", { replace: true })} />;
    case "cookies":
      return <FirstUseCookieGate />;
    case "password":
      return (
        <ChangePassword
          intent="create"
          firstUseNextStage="details"
          firstUseReturnTo="/onboarding/doctor?first_use=1&stage=details"
          firstUseSignOutAfterCreate={false}
        />
      );
    case "category":
      // StepCategory owns the locked clinician presentation and its
      // BasicDialog confirmation. Keep it at the shared route so no
      // client-held role can influence the selected lane.
      return <Navigate to="/onboarding?first_use=1&stage=category" replace />;
    case "details":
      return <DoctorOnboarding backToCategory={backToCategory} />;
    default:
      return <RouteFallback label="Preparing your AfriMed™ setup..." />;
  }
}
